import React, { useCallback, useState } from "react";

import CustomButton from "components/common/Button";
import InlineLoader from "./InlineLoader";
import { listBillingInvoices } from "services/billing";
import type { BsPaginated } from "services/billing";

import CustomerFilter from "./CustomerFilter";
import FilterSelect from "./FilterSelect";
import InvoiceDetailModal from "./InvoiceDetailModal";
import InvoiceStatusChip from "./InvoiceStatusChip";
import { useCursorList } from "./useCursorList";
import { TableComponent } from "./ui";
import {
  BsInvoice,
  INVOICE_STATUS_OPTIONS,
  INVOICE_TYPE_OPTIONS,
  ON_BS_SINCE_LABEL,
  formatDate,
  formatMoney,
  titleCase,
} from "./types";

const columns = [
  {
    id: "status",
    label: "Status",
    render: (v: string, row: BsInvoice) => <InvoiceStatusChip status={v} metadata={row.metadata} />,
  },
  { id: "type", label: "Type", render: (v: string) => titleCase(v) },
  { id: "totalAmountCents", label: "Amount", render: (v: number, row: BsInvoice) => formatMoney(v, row.currency) },
  { id: "dueDate", label: "Due", render: (v: string) => formatDate(v) },
  { id: "paidAt", label: "Paid", render: (v: string) => formatDate(v) },
  { id: "createdAt", label: ON_BS_SINCE_LABEL, render: (v: string) => formatDate(v) },
];

export default function InvoicesTab() {
  // [PRD-3791] Customer, status and type filters.
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const load = useCallback(
    (cursor?: string) =>
      listBillingInvoices({
        customerId,
        status: status || undefined,
        type: type || undefined,
        cursor,
        limit: 25,
      }) as Promise<BsPaginated<BsInvoice>>,
    [customerId, status, type]
  );
  const { rows, hasMore, loading, loadingMore, error, loadMore } = useCursorList<BsInvoice>(load);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1 items-end" style={{ flexWrap: "wrap" }}>
        <CustomerFilter onChange={setCustomerId} />
        <FilterSelect name="invoices-status" label="Status" options={INVOICE_STATUS_OPTIONS} value={status} onChange={setStatus} />
        <FilterSelect name="invoices-type" label="Type" options={INVOICE_TYPE_OPTIONS} value={type} onChange={setType} />
      </div>
      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : (
        <>
          <TableComponent columns={columns} data={rows} disableSort onRowClick={(row: BsInvoice) => setSelectedId(row.id)} />
          {hasMore && (
            <div className="flex justify-center mt-05">
              <CustomButton label={loadingMore ? "Loading…" : "Load more"} onClick={loadMore} disabled={loadingMore} />
            </div>
          )}
        </>
      )}
      {selectedId && <InvoiceDetailModal invoiceId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}
